import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getAllburgers,
  getSingleBurger,
  createBurger,
  updateBurger,
  deleteBurger,
} from "@/api/new-api/staff-management/menu-manage/burger";
import { websiteOrderingAvailableItemsToggle } from "@/api/new-api/staff-management/menu-manage/drink";

// Thunks

export const fetchAllBurgers = createAsyncThunk(
  "burgers/fetchAll",
  async (restaurant_id, { rejectWithValue }) => {
    const res = await getAllburgers(restaurant_id);
    if (res.status === "success") return res.data.burgers;
    return rejectWithValue(res.message);
  }
);

export const fetchSingleBurger = createAsyncThunk(
  "burgers/fetchOne",
  async (id, { rejectWithValue }) => {
    const res = await getSingleBurger(id);
    if (res.status === "success") return res.data.burger;
    return rejectWithValue(res.message);
  }
);

export const createNewBurger = createAsyncThunk(
  "burgers/create",
  async (data, { rejectWithValue }) => {
    const res = await createBurger(data);
    if (res.status === "success") return res.data.burger;
    return rejectWithValue(res.message);
  }
);

export const updateExistingBurger = createAsyncThunk(
  "burgers/update",
  async ({ id, data }, { rejectWithValue }) => {
    const res = await updateBurger(id, data);
    if (res.status === "success") return res.data.burger;
    return rejectWithValue(res.message);
  }
);

export const deleteBurgerById = createAsyncThunk(
  "burgers/delete",
  async (id, { rejectWithValue }) => {
    const res = await deleteBurger(id);
    if (res.status === "success") return id;
    return rejectWithValue(res.message);
  }
);

export const updateBurgerToggle = createAsyncThunk("burgerToggle/update", async ({ id, type, toggleAvailabilityType }, { rejectWithValue }) => {
    const res = await websiteOrderingAvailableItemsToggle(id, type, toggleAvailabilityType);
    if (res.status === "success") return res.data;
    return rejectWithValue(res.message);
});

// Initial State

const initialState = {
  burgers: [],
  singleBurger: null,

  getLoading: false,
  createLoading: false,
  updateLoading: false,
  deleteLoading: false,

  success: null,
  error: null,
  requestStatus: false,
};

// Slice

const burgerSlice = createSlice({
  name: "burgers",
  initialState,
  reducers: {
    clearBurgerMessages: (state) => {
      state.success = null;
      state.error = null;
      state.requestStatus = false;
    },
  },
  extraReducers: (builder) => {
    builder

      // Fetch All
      .addCase(fetchAllBurgers.pending, (state) => {
        state.getLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchAllBurgers.fulfilled, (state, action) => {
        state.getLoading = false;
        state.burgers = action.payload;
        state.requestStatus = true;
      })
      .addCase(fetchAllBurgers.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Fetch Single
      .addCase(fetchSingleBurger.pending, (state) => {
        state.getLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchSingleBurger.fulfilled, (state, action) => {
        state.getLoading = false;
        state.singleBurger = action.payload;
        state.requestStatus = true;
      })
      .addCase(fetchSingleBurger.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Create
      .addCase(createNewBurger.pending, (state) => {
        state.createLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(createNewBurger.fulfilled, (state, action) => {
        state.createLoading = false;
        state.burgers.push(action.payload);
        state.success = "Burger created successfully!";
        state.requestStatus = true;
      })
      .addCase(createNewBurger.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Update
      .addCase(updateExistingBurger.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(updateExistingBurger.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.burgers = state.burgers.map((burger) =>
          burger.id === action.payload.id ? action.payload : burger
        );
        state.success = "Burger updated successfully!";
        state.requestStatus = true;
      })
      .addCase(updateExistingBurger.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Delete
      .addCase(deleteBurgerById.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(deleteBurgerById.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.burgers = state.burgers.filter(
          (burger) => burger.id !== action.payload
        );
        state.success = "Burger deleted successfully!";
        state.requestStatus = true;
      })
      .addCase(deleteBurgerById.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // toggle website/QRcode availability
      .addCase(updateBurgerToggle.pending, (state) => {
          state.updateLoading = true;
          state.error = null;
          state.requestStatus = false;
      })
      .addCase(updateBurgerToggle.fulfilled, (state, action) => {
        state.updateLoading = false;

        const { id, toggleAvailabilityType } = action.meta.arg;

        state.burgers = state.burgers.map((drink) => {
          if (drink.id !== id) return drink;

          if (toggleAvailabilityType === "qr_code") {
            return {
              ...drink,
              is_qr_code_order_available: !drink.is_qr_code_order_available,
            };
          }

          return {
            ...drink,
            is_website_order_available: !drink.is_website_order_available,
          };
        });

        state.success = "Updated successfully!";
        state.requestStatus = true;
      })
      .addCase(updateBurgerToggle.rejected, (state, action) => {
          state.updateLoading = false;
          state.error = action.payload;
          state.requestStatus = false;
      })
  },
});

export const { clearBurgerMessages } = burgerSlice.actions;
export default burgerSlice.reducer;
